// AI API client — wraps /api/ai/* endpoints.

import { getAuth } from 'firebase/auth';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

async function authHeaders() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function parseError(res) {
  const data = await res.json().catch(() => ({}));
  const err = new Error(data.message || `Request failed: ${res.status}`);
  err.status = res.status;
  err.code = data.code;
  err.body = data;
  return err;
}

/**
 * Upload a PDF once, get back a documentHash. Subsequent tool calls can pass
 * the hash instead of re-uploading, benefiting from the 1hr extraction cache.
 */
export async function extractText(file) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API}/api/ai/extract-text`, { method: 'POST', headers, body: form });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/**
 * Call a tool endpoint. Automatically picks file vs documentHash based on args.
 */
async function callTool(path, { file, documentHash, ...fields }) {
  const headers = await authHeaders();
  let body;
  if (file) {
    body = new FormData();
    body.append('file', file);
    for (const [k, v] of Object.entries(fields)) {
      if (v !== undefined && v !== null) body.append(k, String(v));
    }
  } else if (documentHash) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ documentHash, ...fields });
  } else {
    throw new Error('Either file or documentHash is required.');
  }

  const res = await fetch(`${API}${path}`, { method: 'POST', headers, body });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export const aiChat = (args) => callTool('/api/ai/chat', args);
export const aiSummarize = (args) => callTool('/api/ai/summarize', args);
export const aiTranslate = (args) => callTool('/api/ai/translate', args);
export const aiQuestions = (args) => callTool('/api/ai/questions', args);
export const aiAssistant = (args) => callTool('/api/ai/assistant', args);

export async function aiUsage() {
  const headers = await authHeaders();
  const res = await fetch(`${API}/api/ai/usage`, { headers });
  if (!res.ok) throw await parseError(res);
  return res.json();
}
